import { create } from 'zustand';
import { absencesApi } from '../../../api/api';
import type { AppAbsenceDto, AppCreateAbsenceDto, AbsenceType } from '../../../api/generated';

type AbsenceState = {
  absences: AppAbsenceDto[];
  currentAbsences: AppAbsenceDto[];
  loading: boolean;
  error: string | null;

  // Actions
  loadAbsences: () => Promise<void>;
  loadCurrentAbsences: () => Promise<void>;
  loadAbsencesByUser: (userId: string) => Promise<void>;
  loadAbsencesByType: (type: AbsenceType) => Promise<void>;
  loadAbsencesByUserAndType: (userId: string, type: AbsenceType) => Promise<void>;

  createAbsence: (absenceData: AppCreateAbsenceDto) => Promise<AppAbsenceDto>;
  updateAbsence: (id: string, absenceData: AppCreateAbsenceDto) => Promise<AppAbsenceDto>;
  deleteAbsence: (id: string) => Promise<void>;
  restoreAbsence: (id: string) => Promise<AppAbsenceDto>;

  clearError: () => void;
};

export const useAbsenceStore = create<AbsenceState>(set => ({
  // State
  absences: [],
  currentAbsences: [],
  loading: false,
  error: null,

  // Actions
  loadAbsences: async () => {
    set({ loading: true, error: null });

    try {
      const response = await absencesApi.getCurrentAbsences();
      set({ absences: response.data, loading: false });
    } catch (error: any) {
      console.error('Chyba při načítání absencí:', error);
      set({
        error: error.response?.data?.message || 'Nepodařilo se načíst absence',
        loading: false,
      });
    }
  },

  loadCurrentAbsences: async () => {
    set({ loading: true, error: null });

    try {
      const response = await absencesApi.getCurrentAbsences();
      set({ currentAbsences: response.data, loading: false });
    } catch (error: any) {
      console.error('Chyba při načítání aktuálních absencí:', error);
      set({
        error: error.response?.data?.message || 'Nepodařilo se načíst aktuální absence',
        loading: false,
      });
    }
  },

  loadAbsencesByUser: async (userId: string) => {
    set({ loading: true, error: null });

    try {
      const response = await absencesApi.getAbsencesByUser({ userId });
      set({ absences: response.data, loading: false });
    } catch (error: any) {
      console.error('Chyba při načítání absencí zaměstnance:', error);
      set({
        error: error.response?.data?.message || 'Nepodařilo se načíst absence zaměstnance',
        loading: false,
      });
    }
  },

  loadAbsencesByType: async (type: AbsenceType) => {
    set({ loading: true, error: null });

    try {
      const response = await absencesApi.getAbsencesByType({ type });
      set({ absences: response.data, loading: false });
    } catch (error: any) {
      console.error('Chyba při načítání absencí podle typu:', error);
      set({
        error: error.response?.data?.message || 'Nepodařilo se načíst absence podle typu',
        loading: false,
      });
    }
  },

  loadAbsencesByUserAndType: async (userId: string, type: AbsenceType) => {
    set({ loading: true, error: null });

    try {
      const response = await absencesApi.getAbsencesByUserAndType({
        userId,
        type,
      });
      set({ absences: response.data, loading: false });
    } catch (error: any) {
      console.error('Chyba při načítání absencí zaměstnance podle typu:', error);
      set({
        error: error.response?.data?.message || 'Nepodařilo se načíst absence zaměstnance podle typu',
        loading: false,
      });
    }
  },

  createAbsence: async (absenceData: AppCreateAbsenceDto) => {
    set({ loading: true, error: null });

    try {
      const response = await absencesApi.createAbsence({ appCreateAbsenceDto: absenceData });
      const newAbsence = response.data;

      set(state => ({
        absences: [...state.absences, newAbsence],
        currentAbsences: [...state.currentAbsences, newAbsence],
        loading: false,
      }));

      return newAbsence;
    } catch (error: any) {
      console.error('Chyba při vytváření absence:', error);
      set({
        error: error.response?.data?.message || 'Nepodařilo se vytvořit absenci',
        loading: false,
      });
      throw error;
    }
  },

  updateAbsence: async (id: string, absenceData: AppCreateAbsenceDto) => {
    set({ loading: true, error: null });

    try {
      const response = await absencesApi.updateAbsence({
        absenceId: id,
        appCreateAbsenceDto: absenceData,
      });
      const updatedAbsence = response.data;

      set(state => ({
        absences: state.absences.map(absence => (absence.id === id ? updatedAbsence : absence)),
        currentAbsences: state.currentAbsences.map(absence => (absence.id === id ? updatedAbsence : absence)),
        loading: false,
      }));

      return updatedAbsence;
    } catch (error: any) {
      console.error('Chyba při aktualizaci absence:', error);
      set({
        error: error.response?.data?.message || 'Nepodařilo se aktualizovat absenci',
        loading: false,
      });
      throw error;
    }
  },

  deleteAbsence: async (id: string) => {
    set({ loading: true, error: null });

    try {
      await absencesApi.deleteAbsence({ absenceId: id });

      set(state => ({
        absences: state.absences.filter(absence => absence.id !== id),
        currentAbsences: state.currentAbsences.filter(absence => absence.id !== id),
        loading: false,
      }));
    } catch (error: any) {
      console.error('Chyba při mazání absence:', error);
      set({
        error: error.response?.data?.message || 'Nepodařilo se smazat absenci',
        loading: false,
      });
      throw error;
    }
  },

  restoreAbsence: async (id: string) => {
    set({ loading: true, error: null });

    try {
      const response = await absencesApi.restoreAbsence({ absenceId: id });
      const restoredAbsence = response.data;

      set(state => ({
        absences: [...state.absences, restoredAbsence],
        currentAbsences: [...state.currentAbsences, restoredAbsence],
        loading: false,
      }));

      return restoredAbsence;
    } catch (error: any) {
      console.error('Chyba při obnovení absence:', error);
      set({
        error: error.response?.data?.message || 'Nepodařilo se obnovit absenci',
        loading: false,
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
